import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import {
  ImpersonationInfo,
  stopImpersonation as apiStopImpersonation,
  getImpersonationStatus
} from '~/services/superAdminService'
import { useUserContext } from './UserContext'
import { isSuperAdminSubdomain } from '~/utils/utils'

// Key for storing the return URL in localStorage
const IMPERSONATION_RETURN_URL_KEY = 'impersonation_return_url'

/**
 * Save the URL to return to after impersonation ends.
 * Call this before starting impersonation.
 */
export function setImpersonationReturnUrl(url: string): void {
  localStorage.setItem(IMPERSONATION_RETURN_URL_KEY, url)
}

/**
 * Get and clear the saved return URL.
 */
function getAndClearReturnUrl(): string | null {
  const url = localStorage.getItem(IMPERSONATION_RETURN_URL_KEY)
  if (url) {
    localStorage.removeItem(IMPERSONATION_RETURN_URL_KEY)
  }
  return url
}

interface ImpersonationContextValue {
  isImpersonating: boolean
  impersonatedUserEmail: string | null
  impersonatedUserName: string | null
  impersonatedUserId: number | null
  adminUserEmail: string | null
  loading: boolean
  error: string | null
  stopImpersonation: () => Promise<void>
  refetch: () => void
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null)

interface ImpersonationProviderProps {
  children: React.ReactNode
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const [info, setInfo] = useState<ImpersonationInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user } = useUserContext()

  // Only fetch impersonation status if user profile indicates impersonation is active
  const shouldFetchStatus = user?.is_impersonating === true

  const fetchStatus = useCallback(async () => {
    if (!shouldFetchStatus) {
      setInfo(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const status = await getImpersonationStatus()
      setInfo(status)
    } catch {
      // If not a super admin, the request will fail - that's expected
      setInfo(null)
      // Don't set error for expected failures (non-super admins)
    } finally {
      setLoading(false)
    }
  }, [shouldFetchStatus])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const stopImpersonation = useCallback(async () => {
    try {
      const result = await apiStopImpersonation()
      setInfo({
        status: {
          is_impersonating: false,
          impersonated_user_id: null,
          impersonated_user_email: null,
          impersonated_user_name: null,
          admin_user_id: null,
          admin_user_email: null,
          return_url: null
        },
        workspaces: []
      })

      // Navigate back to return URL (from API response, localStorage, or default)
      // Priority: 1) API response return_url, 2) localStorage, 3) default
      const returnUrl =
        result.return_url ||
        getAndClearReturnUrl() ||
        (isSuperAdminSubdomain() ? '/dashboard' : '/super-admin')

      window.location.href = returnUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop impersonation')
    }
  }, [])

  const value: ImpersonationContextValue = {
    isImpersonating: info?.status.is_impersonating ?? false,
    impersonatedUserEmail: info?.status.impersonated_user_email ?? null,
    impersonatedUserName: info?.status.impersonated_user_name ?? null,
    impersonatedUserId: info?.status.impersonated_user_id ?? null,
    adminUserEmail: info?.status.admin_user_email ?? null,
    loading,
    error,
    stopImpersonation,
    refetch: fetchStatus
  }

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
}

export function useImpersonation(): ImpersonationContextValue {
  const context = useContext(ImpersonationContext)
  if (!context) {
    // Return a default non-impersonating state if used outside provider
    return {
      isImpersonating: false,
      impersonatedUserEmail: null,
      impersonatedUserName: null,
      impersonatedUserId: null,
      adminUserEmail: null,
      loading: false,
      error: null,
      stopImpersonation: async () => {},
      refetch: () => {}
    }
  }
  return context
}
